import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Monitor, Check } from 'lucide-react';

const OPTIONS = [
  { id: 'paper', Icon: FileText },
  { id: 'digital', Icon: Monitor },
];

/**
 * Paper or digital, with a sentence on what each means. Shown before a mock
 * exam starts, on the try-an-exam page and once in onboarding. Paper is the
 * recommended (default) mode.
 *
 * `tone="dark"` for dark surfaces (onboarding); `compact` drops the heading;
 * `stacked` puts the options one above the other (narrow containers).
 */
const ExamModePicker = ({ value, onChange, tone = 'light', compact = false, stacked = false, className = '' }) => {
  const { t } = useTranslation();
  const dark = tone === 'dark';

  return (
    <div className={className}>
      {!compact && (
        <h2 className={`font-space text-lg sm:text-xl font-black tracking-tight mb-3 ${dark ? 'text-white' : 'text-black'}`}>
          {t('examMode.title')}
        </h2>
      )}
      <div role="radiogroup" aria-label={t('examMode.title')} className={`grid gap-3 ${stacked ? '' : 'sm:grid-cols-2'}`}>
        {OPTIONS.map(({ id, Icon }) => {
          const selected = value === id;
          const base = dark
            ? (selected ? 'border-white bg-white text-black' : 'border-white/25 bg-white/5 text-white hover:border-white/60')
            : (selected ? 'border-black bg-black text-white shadow-lg' : 'border-black/15 bg-white text-black hover:border-black/60');
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(id)}
              className={`relative flex items-start gap-3 text-left rounded-2xl border-2 p-4 min-h-[48px] transition-all duration-200 active:scale-[0.98] touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3A86FF] ${base}`}
            >
              <span className={`flex-none inline-flex items-center justify-center w-10 h-10 rounded-xl ${selected ? (dark ? 'bg-black/5' : 'bg-white/15') : (dark ? 'bg-white/10' : 'bg-black/5')}`}>
                <Icon className="w-5 h-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="font-space font-bold text-base">{t(`examMode.${id}.title`)}</span>
                  {id === 'paper' && (
                    <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${selected ? (dark ? 'bg-black text-white' : 'bg-[#3A86FF] text-white') : 'bg-[#3A86FF]/10 text-[#1d4ed8]'}`}>
                      {t('examMode.recommended')}
                    </span>
                  )}
                </span>
                <span className={`block mt-1 text-sm leading-snug ${selected ? (dark ? 'text-black/70' : 'text-white/80') : (dark ? 'text-white/70' : 'text-black/60')}`}>
                  {t(`examMode.${id}.body`)}
                </span>
              </span>
              {selected && (
                <span className={`absolute top-3 right-3 inline-flex items-center justify-center w-5 h-5 rounded-full ${dark ? 'bg-black text-white' : 'bg-white text-black'}`} aria-hidden="true">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ExamModePicker;
